<template>
  <div v-if="isDataLoaded" :class="{ 'all-wrap': true, '--wide': wideMode }">
    <Sidebar @viewchange="changeViewMode()" />
    <Notifications />
    <div class="content">
      <!-- LLM:METADATA
      // :topology: [Domain:Navigation | Role:Layout | Pattern:GlobalBreadcrumbs]
      // :relates-to: uses: "BreadCrumbs", "useBreadcrumbs", required-by: "All Pages"
      // :rationale: "Global breadcrumbs display in main layout for consistent navigation"
      // :contract: pre: "isDataLoaded true", post: "Breadcrumbs rendered above router-view", invariant: "Home always first item"
      // :decision_cache: "Chose layout-level breadcrumbs for DRY principle [LAYOUT-001]"
      // LLM:END -->
      <BreadCrumbs v-if="showBreadcrumbs" :items="breadcrumbItems" />

      <!-- @artist-hover="makeWhite()"
        @normal-state="makeNormal()" -->
      <router-view v-slot="{ Component, route }">
        <transition :name="route.meta.transition || 'pages-fade'">
          <component :is="Component" />
        </transition>
      </router-view>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed } from "vue"
import { useStore } from "vuex"
import { useRoute, useRouter } from "vue-router"
import { useAuth } from "@/composables/useAuth"
import { useAppInit } from "@/composables/useAppInit"
import { useBreadcrumbs } from "@/composables/useBreadcrumbs"
import { fetchCsrfToken } from "@/services/csrf"
import { resolveEditorRuntimeMode } from "@/services/api"
import { listenForAuthRequests } from "@/services/crossTabAuthBridge"
import Sidebar from "./components/Sidebar.vue"
import Notifications from "@/components/Notifications.vue"
import BreadCrumbs from "@/components/BreadCrumbs.vue"

// LLM:METADATA
// :topology: [Domain:Core | Role:RootComponent | Pattern:Composition]
// :relates-to: uses: "useAuth", "useAppInit", "useBreadcrumbs", "Sidebar", "Notifications", "BreadCrumbs"
// :rationale: "Root component with global breadcrumbs integration. Orchestrates Auth, Data Loading, and Navigation with auth-ready barrier."
// :contract: pre: "Mounted", post: "Auth checked, Data loaded, Breadcrumbs rendered, UI displayed", invariant: "Content only shows after auth status resolved"
// LLM:END

const store = useStore()
const route = useRoute()
const router = useRouter()
const { checkToken } = useAuth()
const { initAppData, isDataLoaded } = useAppInit()
const { items: breadcrumbItems } = useBreadcrumbs()

const wideMode = ref(false)

// Cross-tab auth bridge listener (started once per authenticated tab)
let authBridgeStopper = null

// LLM:METADATA
// :topology: [Domain:Authentication | Role:AuthReadyGate | Pattern:StateBarrier]
// :relates-to: uses: "store.getters.getAuthStatus", required-by: "isDataLoaded logic", "all child views"
// :rationale: "Blocks content rendering until auth status is resolved (authenticated or unauthenticated). Prevents protected API fan-out before auth validation."
// :contract: pre: "Auth state initialized", post: "Returns boolean", invariant: "Returns true only when authStatus is final (authenticated|unauthenticated)"
// :decision_cache: "Chose computed property for reactive auth-ready signal [APP-AUTH-001]"
// LLM:END
const authReady = computed(() => {
  const authStatus = store.getters.getAuthStatus()
  return authStatus === "authenticated" || authStatus === "unauthenticated"
})

// LLM:METADATA
// :topology: [Domain:Navigation | Role:VisibilityController | Pattern:Computed]
// :relates-to: uses: "route.name", required-by: "BreadCrumbs visibility"
// :rationale: "Hides breadcrumbs on login page for cleaner UX"
// :contract: pre: "Route resolved", post: "Returns boolean", invariant: "Always false for login route"
// :decision_cache: "Chose computed property for reactive breadcrumbs visibility [LAYOUT-002]"
// LLM:END
const showBreadcrumbs = computed(() => {
  return route.name !== "login"
})

const changeViewMode = () => {
  wideMode.value = !wideMode.value
}

// Global Event Listener
const handleScroll = () => {}

onMounted(async () => {
  window.addEventListener("scroll", handleScroll)
  localStorage.setItem("startUrl", window.location.href)

  // Start cross-tab auth bridge listener to respond to new tab requests
  // This allows authenticated tabs to share their session token with newly opened tabs
  authBridgeStopper = listenForAuthRequests()

  const domain = window.location.hostname
  const runtimeMode = resolveEditorRuntimeMode(domain)

  if (runtimeMode === "local") {
    store.dispatch("setLocalMode")
  } else if (runtimeMode === "editor-dev") {
    store.dispatch("setEditorDevMode")
  } else if (runtimeMode === "editor-prod") {
    store.dispatch("setEditorProdMode")
  } else {
    store.dispatch("setDevMode")
  }

  // LLM:METADATA
  // :topology: [Domain:Authentication | Role:BootstrapBarrier | Pattern:StateGate]
  // :relates-to: uses: "checkToken", "authReady", required-by: "isDataLoaded"
  // :rationale: "Waits for auth status to resolve before showing any content. Prevents race conditions with protected API calls."
  // :contract: pre: "App mounted", post: "Auth status resolved, Content ready to show", invariant: "isDataLoaded only true after authReady"
  // :decision_cache: "Chose explicit await of checkToken + status verification for deterministic bootstrap [APP-BOOTSTRAP-001]"
  // LLM:END

  // Step 1: Perform auth check
  await checkToken()

  // Step 2: Wait for router to be ready
  await router.isReady()

  // Step 3: Wait for auth status to be fully resolved
  // The checkToken() call updates authStatus, but we need to ensure it's settled
  // Use a small timeout to let the state propagate (synchronous dispatch should be immediate)
  // In production, this is effectively instant, but ensures consistency
  await new Promise((resolve) => setTimeout(resolve, 0))

  // Step 4: Verify auth is ready and load app data
  // Note: authReady computed ensures we have a final auth state
  if (!authReady.value) {
    console.warn(
      "[App] Auth not ready after checkToken, this should not happen"
    )
    isDataLoaded.value = true
    return
  }

  const authStatus = store.getters.getAuthStatus()
  const isLoginPage = route.name === "login"

  if (authStatus === "authenticated" && !isLoginPage) {
    // Authenticated and not on login page: bootstrap CSRF token before any write path
    await fetchCsrfToken()
    await initAppData()
  } else {
    // Either unauthenticated (show login) or on login page (already loaded)
    // Set loaded to true so router-view can render
    isDataLoaded.value = true
  }
})

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll)

  // Stop cross-tab auth bridge listener when tab closes
  if (authBridgeStopper) {
    authBridgeStopper.stop()
    authBridgeStopper = null
  }
})
</script>
